import { ModalsStyles } from "@omni/ui/brand/modals/modals.styles";
import { ButtonComponentProps } from "@omni/ui/components/Button/button.props";
import { isDefined } from "@omni/utils/helper.utils";
import { createContext, ReactNode, use, useEffect, useState } from "react";
import { SheetDetent } from "react-modal-sheet";

import { ModalType } from "@/redux/modules/value/modal/modal.types";

type ModalContextValue = {
    modalType?: ModalType;
    sheetDetent: SheetDetent;
    title: string | ReactNode;
    subtitle?: string | ReactNode;
    size: "xs" | "sm" | "md" | "lg" | "xl";
    confirmButtonText?: string;
    confirmButtonColor: ButtonComponentProps["color"];
    cancelButtonText?: string;
    open: boolean;
    active?: boolean;
    optimisticOpen: boolean;
    closable: boolean;
    formId?: string;
    isLoading?: boolean;
    isConfirmDisabled: boolean;
    withFooter: boolean;
    centered: boolean;
    autoFocus: boolean;
    scrollable: boolean;
    onConfirm?: () => void;
    onCancel?: () => void;
    setOpen?: (newState: boolean) => void;
    onClose: () => void;
    onCloseEnd: () => void;
    onOpenEnd?: () => void;
};

type ModalContextProviderProps = {
    modalType?: ModalType;
    sheetDetent?: SheetDetent;
    title: string | ReactNode;
    subtitle?: string | ReactNode;
    size?: "xs" | "sm" | "md" | "lg" | "xl";
    confirmButtonText?: string;
    confirmButtonColor?: ButtonComponentProps["color"];
    cancelButtonText?: string;
    open: boolean;
    active?: boolean;
    closable?: boolean;
    formId?: string;
    isLoading?: boolean;
    isConfirmDisabled?: boolean;
    withFooter?: boolean;
    centered?: boolean;
    autoFocus?: boolean;
    scrollable?: boolean;
    onConfirm?: () => void;
    onCancel?: () => void;
    setOpen?: (newState: boolean) => void;
    onOpenEnd?: () => void;
    onCloseEnd?: () => void;
    children: ReactNode;
};

export const ModalContext = createContext<ModalContextValue | undefined>(
    undefined
);
ModalContext.displayName = "ModalContext";

export const ModalContextProvider = ({
    modalType,
    sheetDetent = "content",
    title,
    subtitle,
    size = "lg",
    confirmButtonText,
    confirmButtonColor,
    cancelButtonText,
    open,
    active = true,
    closable = true,
    formId,
    isLoading,
    isConfirmDisabled = false,
    withFooter = true,
    centered = true,
    autoFocus = true,
    scrollable = false,
    onConfirm,
    onCancel,
    setOpen,
    onOpenEnd,
    onCloseEnd,
    children
}: ModalContextProviderProps) => {
    // Optimistic state protects the component from unmounting before the animation is finished
    // is the parent component renders the sheet conditionally
    const [optimisticOpen, setOptimisticOpen] = useState(open);

    useEffect(() => {
        setOptimisticOpen(open);
    }, [open]);

    const handleOnClose = () => {
        setOptimisticOpen(false);
    };

    const handleOnCloseEnd = () => {
        onCloseEnd?.();
        setOpen?.(false);
    };

    return (
        <ModalContext
            value={{
                modalType: modalType,
                sheetDetent: sheetDetent,
                title: title,
                subtitle: subtitle,
                size: size,
                confirmButtonText: confirmButtonText,
                confirmButtonColor:
                    confirmButtonColor ||
                    ModalsStyles.confirmButton?.color ||
                    "success",
                cancelButtonText: cancelButtonText,
                open: open,
                active: active,
                optimisticOpen: optimisticOpen,
                formId: formId,
                isLoading: isLoading,
                isConfirmDisabled: isConfirmDisabled,
                withFooter: withFooter,
                autoFocus: autoFocus,
                closable: closable,
                centered: centered,
                scrollable: scrollable,
                onConfirm: onConfirm,
                onCancel: onCancel,
                setOpen: setOpen,
                onClose: handleOnClose,
                onCloseEnd: handleOnCloseEnd,
                onOpenEnd: onOpenEnd
            }}
        >
            {children}
        </ModalContext>
    );
};

export const useModalContext = () => {
    const context = use<ModalContextValue | undefined>(ModalContext);

    if (!isDefined(context)) {
        throw new Error(`${ModalContext.displayName} is undefined.`);
    }

    return context;
};

export const useIsModal = () => {
    const context = use<ModalContextValue | undefined>(ModalContext);

    return isDefined(context);
};
